// Departments Slider — renders top-level departments as a carousel (5 per slide,
// prev/next arrows, swipe). Pure JS carousel (no jQuery/Slick dependency); it only
// borrows the theme's slick-arrow classes for styling.
//
// Config (read from the TARGET element, not the script tag):
//   [data-parts-departments-slider]  — the container (supports MULTIPLE per page)
//
// apiKey / base URL come from shared config; the fetch goes through apiJson so the
// Bearer key is attached automatically.

import { getTopDocument } from '../shared/dom.js';
import { getAppBaseUrl } from '../shared/config.js';
import { apiJson } from '../shared/api.js';

var CONTAINER_SELECTOR = '[data-parts-departments-slider]';

// ---------------------------------------------------------------------------
// Department → image mapping (mirrors departmentIcons.ts)
// ---------------------------------------------------------------------------
var DEPT_MAPPINGS = [
  { slug: '09', aliases: ['engine drive'] },
  { slug: '06', aliases: ['drive train', 'drivetrain', 'transmission', 'gearbox', 'clutch'] },
  { slug: '04', aliases: ['control cables', 'control cable', 'cables'] },
  { slug: '05', aliases: ['cooling ac', 'cooling', 'ac', 'air con', 'air conditioning', 'radiator', 'coolant'] },
  { slug: '10', aliases: ['exhaust systems', 'exhaust system', 'exhaust', 'muffler'] },
  { slug: '13', aliases: ['fuel systems', 'fuel system', 'fuel'] },
  { slug: '01', aliases: ['all departments', 'all'] },
  { slug: '20', aliases: ['accessories', 'accessory', 'extras', 'add-ons', 'add-on'] },
  { slug: '02', aliases: ['body', 'body parts', 'panels', 'exterior'] },
  { slug: '03', aliases: ['brakes', 'brake', 'brake pads', 'brake pad'] },
  { slug: '07', aliases: ['electrical', 'electronics', 'battery', 'batteries', 'ignition', 'spark plug', 'spark plugs', 'lighting', 'lights', 'light', 'bulb', 'bulbs'] },
  { slug: '08', aliases: ['engine', 'engines', 'motor', 'mechanical'] },
  { slug: '11', aliases: ['filters', 'filter', 'oil filter', 'air filter', 'fuel filter'] },
  { slug: '12', aliases: ['fluids', 'fluid', 'oil', 'oils', 'lubricants', 'lubricant'] },
  { slug: '14', aliases: ['gaskets', 'gasket', 'seals', 'seal'] },
  { slug: '15', aliases: ['mountings', 'mounting', 'mounts', 'mount', 'bushings', 'bushing'] },
  { slug: '16', aliases: ['steering'] },
  { slug: '17', aliases: ['suspension', 'shock', 'shocks', 'shock absorber', 'strut', 'struts'] },
  { slug: '18', aliases: ['tools', 'tool', 'hand tools'] },
  { slug: '19', aliases: ['wheels', 'wheel', 'tires', 'tire', 'tyres', 'tyre'] },
];

function getDeptImageUrl(name) {
  var base = getAppBaseUrl().replace(/\/$/, '');
  var norm = (name || '').toLowerCase().trim().replace(/_/g, ' ').replace(/\s+/g, ' ');
  var slug = null;

  // 1. exact alias match
  for (var i = 0; i < DEPT_MAPPINGS.length; i++) {
    if (DEPT_MAPPINGS[i].aliases.indexOf(norm) !== -1) { slug = DEPT_MAPPINGS[i].slug; break; }
  }
  // 2. contains match
  if (!slug) {
    for (var i = 0; i < DEPT_MAPPINGS.length; i++) {
      for (var j = 0; j < DEPT_MAPPINGS[i].aliases.length; j++) {
        var alias = DEPT_MAPPINGS[i].aliases[j];
        if (norm.indexOf(alias) !== -1 || alias.indexOf(norm) !== -1) { slug = DEPT_MAPPINGS[i].slug; break; }
      }
      if (slug) break;
    }
  }
  // 3. word-by-word
  if (!slug) {
    var words = norm.split(' ').filter(function (w) { return w.length > 3; });
    outer: for (var wi = 0; wi < words.length; wi++) {
      for (var i = 0; i < DEPT_MAPPINGS.length; i++) {
        if (DEPT_MAPPINGS[i].aliases.indexOf(words[wi]) !== -1) { slug = DEPT_MAPPINGS[i].slug; break outer; }
      }
    }
  }

  slug = slug || '01';
  return base + '/assets/dept-img/' + slug + '.png';
}

function slugify(name) {
  return (name || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/g, '').replace(/_+/g, '_').replace(/^_|_$/, '');
}

// ---------------------------------------------------------------------------
// Build the DOM scaffold and return key references
// ---------------------------------------------------------------------------
function buildScaffold(container, sliderId) {
  var doc = getTopDocument();

  var outer = doc.createElement('div');
  outer.className = 'pf-dept-slider-outer';

  // Prev arrow
  var prevBtn = doc.createElement('button');
  prevBtn.type = 'button';
  prevBtn.setAttribute('data-role', 'none');
  prevBtn.className = 'slick-prev slick-arrow slider-prev';
  prevBtn.setAttribute('aria-label', 'Previous');
  prevBtn.setAttribute('role', 'button');
  prevBtn.innerHTML = '<span><i class="fa fa-angle-left"></i></span>';

  // Slider wrap + track
  var wrap = doc.createElement('div');
  wrap.className = 'pf-dept-slider-wrap';

  var track = doc.createElement('div');
  track.className = 'pf-dept-slides-track';
  track.id = sliderId + '-track';

  // Loading state
  var loading = doc.createElement('div');
  loading.className = 'pf-dept-loading';
  loading.innerHTML = '<div class="pf-dept-spinner"></div><p style="margin-top:12px;">Loading departments...</p>';
  track.appendChild(loading);

  wrap.appendChild(track);

  // Next arrow
  var nextBtn = doc.createElement('button');
  nextBtn.type = 'button';
  nextBtn.setAttribute('data-role', 'none');
  nextBtn.className = 'slick-next slick-arrow slider-next';
  nextBtn.setAttribute('aria-label', 'Next');
  nextBtn.setAttribute('role', 'button');
  nextBtn.innerHTML = '<span><i class="fa fa-angle-right"></i></span>';

  outer.appendChild(prevBtn);
  outer.appendChild(wrap);
  outer.appendChild(nextBtn);
  container.appendChild(outer);

  return { outer: outer, wrap: wrap, track: track, prevBtn: prevBtn, nextBtn: nextBtn };
}

// ---------------------------------------------------------------------------
// Carousel logic (pure JS, no jQuery/Slick dependency)
// ---------------------------------------------------------------------------
function initCarousel(track, prevBtn, nextBtn, departments) {
  var perPage = 5;
  var currentPage = 0;
  var total = departments.length;
  var totalPages = Math.ceil(total / perPage);

  function updateButtons() {
    if (currentPage <= 0) {
      prevBtn.classList.add('slick-disabled');
      prevBtn.setAttribute('aria-disabled', 'true');
    } else {
      prevBtn.classList.remove('slick-disabled');
      prevBtn.setAttribute('aria-disabled', 'false');
    }
    if (currentPage >= totalPages - 1) {
      nextBtn.classList.add('slick-disabled');
      nextBtn.setAttribute('aria-disabled', 'true');
    } else {
      nextBtn.classList.remove('slick-disabled');
      nextBtn.setAttribute('aria-disabled', 'false');
    }
  }

  function goTo(page) {
    if (page < 0 || page >= totalPages) return;
    currentPage = page;
    // Each page = 100% width of the wrap
    track.style.transform = 'translateX(-' + (currentPage * 100) + '%)';
    updateButtons();
  }

  prevBtn.addEventListener('click', function () { goTo(currentPage - 1); });
  nextBtn.addEventListener('click', function () { goTo(currentPage + 1); });

  // Swipe support
  var touchStartX = 0;
  track.addEventListener('touchstart', function (e) { touchStartX = e.touches[0].clientX; }, { passive: true });
  track.addEventListener('touchend', function (e) {
    var dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 40) { dx < 0 ? goTo(currentPage + 1) : goTo(currentPage - 1); }
  }, { passive: true });

  updateButtons();
}

// ---------------------------------------------------------------------------
// Render department cards
// ---------------------------------------------------------------------------
function renderDepartments(track, prevBtn, nextBtn, departments) {
  var doc = getTopDocument();
  track.innerHTML = '';

  if (!departments || departments.length === 0) {
    track.innerHTML = '<div style="padding:40px;text-align:center;color:#6c757d;">No departments found.</div>';
    prevBtn.classList.add('slick-disabled');
    nextBtn.classList.add('slick-disabled');
    return;
  }

  departments.forEach(function (dept) {
    var slide = doc.createElement('div');
    slide.className = 'pf-dept-slide';

    var deptSlug = slugify(dept.name || dept.code);
    var href = '/parts-finder?page=1&pageSize=24&sort=relevance&department=' + encodeURIComponent(deptSlug);

    var card = doc.createElement('a');
    card.className = 'pf-dept-card';
    card.href = href;

    // Image
    var imgUrl = getDeptImageUrl(dept.name || dept.code);
    var img = doc.createElement('img');
    img.className = 'pf-dept-card-img';
    img.src = imgUrl;
    img.alt = dept.name || dept.code;
    img.onerror = function () {
      // Replace with a simple placeholder on broken image
      var ph = doc.createElement('div');
      ph.className = 'pf-dept-card-img-placeholder';
      ph.innerHTML = '<i class="fa fa-th" style="font-size:28px;color:#d1d5db;"></i>';
      img.parentNode && img.parentNode.replaceChild(ph, img);
    };
    card.appendChild(img);

    // Name
    var name = doc.createElement('div');
    name.className = 'pf-dept-card-name';
    name.textContent = dept.name || dept.code;
    card.appendChild(name);

    // Part count intentionally NOT rendered — the department slider shows
    // department names only (no per-department part counts).

    slide.appendChild(card);
    track.appendChild(slide);
  });

  initCarousel(track, prevBtn, nextBtn, departments);
}

// ---------------------------------------------------------------------------
// Initialise a single container
// ---------------------------------------------------------------------------
function initOne(container) {
  // Guard against double-init (init() may be dispatched more than once).
  if (container.getAttribute('data-pf-dept-slider-ready') === '1') return;
  container.setAttribute('data-pf-dept-slider-ready', '1');

  var sliderId = 'pf-dept-slider-' + Math.random().toString(36).substr(2, 9);
  var refs = buildScaffold(container, sliderId);

  // Response: { departments: [{ code, name, count }] }. apiJson attaches the
  // Bearer key and resolves to null on any non-OK / parse error.
  apiJson('/api/filters/department', null, null).then(function (data) {
    if (!data) {
      refs.track.innerHTML = '<div style="padding:40px;text-align:center;color:#dc3545;">Could not load departments.</div>';
      console.error('DeptSlider: fetch error');
      return;
    }
    var depts = (data && Array.isArray(data.departments)) ? data.departments : [];
    renderDepartments(refs.track, refs.prevBtn, refs.nextBtn, depts);
  });
}

export function init() {
  var containers = getTopDocument().querySelectorAll(CONTAINER_SELECTOR);
  if (!containers.length) return; // not on a page with this widget
  for (var i = 0; i < containers.length; i++) initOne(containers[i]);
}

export var id = 'departments-slider';
